// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — fondements de retenue (onglet « Retenues ») : enregistrement par une personne
// (ds_fondement_enregistrer), confirmation par une autre (ds_fondement_confirmer, jamais le salarié), consultation.
// Un fondement (cession au greffe, remboursement direct, autre fondement validé par le juriste) est la base juridique
// d'une retenue : l'enregistrer N'OUVRE AUCUN PRÉLÈVEMENT — la transmission à la Paie reste fermée (mode vérification).
// Routes annoncées (ARCHITECTURE_CONSTRUCTION § 10, livrées par l'intégration) : POST /api/dispositifs/paie/fondements,
// POST /fondements/:id/confirmer ; lecture GET /fondements?organisation= (tolérante : « bientôt disponible » si absente).
// Table : rh.ds_fondements_retenue (0048) — nature, référence et pièce du greffe, validité, statut.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { CheckCircle2, FileCheck2, Plus, Undo2 } from 'lucide-react';
import { Bouton, BandeauInfo } from '../../design/composants';
import { toArray, libelle, aujourdhuiDakar } from './api';
import { Champ, Etat, PaginationDs } from './commun';
import { useSocial, dateLongue, dateHeureLongue, NATURES_FONDEMENT, STATUTS_FONDEMENT } from './apiSocial';
import { useLectureSociale, EtatsLecture, FiltreEtats, FenetreActe, ChoixRadio, ChampMotif, motifValide, usePieceDepot, ChampPiece } from './communSocial';
import { ChoixPersonne } from './FenetresMandat';

export function MentionAucunPrelevement() {
  return (
    <BandeauInfo ton="info" titre="Enregistrer un fondement n'ouvre aucun prélèvement">
      Le fondement est la base juridique d'une retenue future. Tant que la transmission à la Paie n'est pas ouverte, les retenues restent proposées et bloquées : rien n'est prélevé sur un salaire.
    </BandeauInfo>
  );
}

const nomPersonne = (s) => [s.prenom, (s.nom || '').toUpperCase()].filter(Boolean).join(' ');

function FenetreFondement({ organisation, onFermer, onFait }) {
  const api = useSocial();
  const [personne, setPersonne] = useState(null);
  const [nature, setNature] = useState('CESSION_GREFFE');
  const [reference, setReference] = useState('');
  const [notification, setNotification] = useState('');
  const [du, setDu] = useState(aujourdhuiDakar());
  const [au, setAu] = useState('');
  const greffe = nature === 'CESSION_GREFFE';
  const piece = usePieceDepot(organisation.id, 'FONDEMENT', null, 'CESSION_GREFFE');
  const avecPiece = greffe || !!piece.saisie.fichier;
  const pret = !!(personne && du && (!au || au >= du) && (!greffe || (reference.trim().length >= 2 && piece.prete)) && (!avecPiece || piece.prete));
  return (
    <FenetreActe titre="Enregistrer un fondement de retenue" Icone={FileCheck2} libelleBouton="Enregistrer" pret={pret} onFermer={onFermer} large
      aide="Une autre personne confirme ensuite le fondement ; le salarié concerné n'enregistre ni ne confirme jamais le sien."
      onConfirmer={async (cle) => {
        const p = avecPiece ? await piece.deposer() : null;
        await api.enregistrerFondement({
          organisation_id: organisation.id, employe_id: personne.id, nature, valide_du: du,
          ...(au ? { valide_au: au } : {}), ...(reference.trim() ? { reference_greffe: reference.trim() } : {}),
          ...(notification ? { date_notification: notification } : {}), ...(p ? { piece_id: p.id } : {}),
        }, cle);
        onFait('Fondement enregistré : il attend la confirmation d\'une autre personne. Aucun prélèvement n\'est ouvert.');
      }}>
      <MentionAucunPrelevement />
      <ChoixPersonne choisie={personne} onChoisir={setPersonne} />
      <ChoixRadio nom="nature-fondement" options={NATURES_FONDEMENT} valeur={nature}
        onChange={(n) => { setNature(n); if (!piece.deposee) piece.setSaisie({ ...piece.saisie, typePiece: n === 'CESSION_GREFFE' ? 'CESSION_GREFFE' : 'CONVENTION' }); }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <Champ libelle="Référence du greffe" requis={greffe}><input className="rh-champ-saisie" maxLength={80} value={reference} onChange={(e) => setReference(e.target.value)} /></Champ>
        <Champ libelle="Notifiée le"><input type="date" className="rh-champ-saisie" value={notification} onChange={(e) => setNotification(e.target.value)} /></Champ>
        <Champ libelle="Valable du" requis><input type="date" className="rh-champ-saisie" value={du} onChange={(e) => setDu(e.target.value)} /></Champ>
        <Champ libelle="Valable au" aide={au && au < du ? 'La fin précède le début.' : 'Facultatif.'}><input type="date" className="rh-champ-saisie" value={au} onChange={(e) => setAu(e.target.value)} /></Champ>
      </div>
      <ChampPiece piece={piece} types={greffe ? ['CESSION_GREFFE'] : ['CONVENTION', 'DECISION', 'JUSTIFICATIF']} libelleChamp={greffe ? 'Acte de cession au greffe (obligatoire)' : 'Pièce (facultative)'} />
    </FenetreActe>
  );
}

function FenetreConfirmerFondement({ f, onFermer, onFait }) {
  const api = useSocial();
  return (
    <FenetreActe titre="Confirmer le fondement" Icone={CheckCircle2} libelleBouton="Confirmer" onFermer={onFermer}
      aide="La personne qui a enregistré le fondement ne le confirme pas (double regard)."
      onConfirmer={async (cle) => { await api.confirmerFondement(f.id, cle); onFait('Fondement confirmé. Aucun prélèvement n\'est ouvert pour autant.'); }}>
      <p style={{ margin: '0 0 12px', fontSize: 14 }}><strong>{f.salarie || f.employe || 'Salarié'}</strong> — {libelle(NATURES_FONDEMENT, f.nature)}{f.reference_greffe ? `, référence ${f.reference_greffe}` : ''}, valable du {dateLongue(String(f.valide_du || '').slice(0, 10))}{f.valide_au ? ` au ${dateLongue(String(f.valide_au).slice(0, 10))}` : ''}.</p>
    </FenetreActe>
  );
}

function FenetreRetirerFondement({ f, onFermer, onFait }) {
  const api = useSocial();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre="Retirer le fondement" Icone={Undo2} libelleBouton="Retirer" danger pret={motifValide(motif)} onFermer={onFermer}
      aide="Un fondement retiré est conservé dans l'historique ; il ne fonde plus aucune retenue."
      onConfirmer={async (cle) => { await api.retirerFondement(f.id, motif.trim(), cle); onFait('Fondement retiré.'); }}>
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

export default function FondementsRetenue({ organisation, droits }) {
  const api = useSocial();
  const [statut, setStatut] = useState('');
  const [page, setPage] = useState(1);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const l = useLectureSociale(() => api.fondements(organisation.id, { page, statut }), [api, organisation.id, page, statut]);
  const lignes = toArray(l.donnees);
  const fait = (m) => { setFenetre(null); setInfo(m); l.recharger(); };
  return (
    <>
      <MentionAucunPrelevement />
      <div className="rh-outils" style={{ justifyContent: 'space-between' }}>
        <FiltreEtats table={STATUTS_FONDEMENT} codes={Object.keys(STATUTS_FONDEMENT)} valeur={statut} onChange={(c) => { setStatut(c); setPage(1); }} libelleGroupe="Filtrer les fondements" />
        {droits.a('ds_fondement_enregistrer') && <Bouton variante="primaire" Icone={Plus} onClick={() => setFenetre({ type: 'enregistrer' })}>Enregistrer un fondement</Bouton>}
      </div>
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      <section className="rh-carte" style={{ padding: 0 }}>
        <EtatsLecture lecture={l} Icone={FileCheck2} estVide={!lignes.length} titreVide={statut ? 'Aucun fondement dans cet état' : 'Aucun fondement enregistré'}
          vide="Sans fondement confirmé, aucune retenue ne peut être admise (motif « fondement absent »).">
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Salarié</th><th>Fondement</th><th>Validité</th><th>État</th><th aria-label="Actions" /></tr></thead>
              <tbody>
                {lignes.map((f) => (
                  <tr key={f.id}>
                    <td><span className="rh-cellule-principale">{f.salarie || (f.employe && nomPersonne(f.employe)) || '—'}</span>{f.matricule && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{f.matricule}</span>}</td>
                    <td>{libelle(NATURES_FONDEMENT, f.nature)}{f.reference_greffe && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>Greffe : {f.reference_greffe}{f.date_notification ? `, notifiée le ${dateLongue(String(f.date_notification).slice(0, 10))}` : ''}</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>du {dateLongue(String(f.valide_du || '').slice(0, 10))}{f.valide_au ? <span className="rh-cellule-secondaire" style={{ display: 'block' }}>au {dateLongue(String(f.valide_au).slice(0, 10))}</span> : <span className="rh-cellule-secondaire" style={{ display: 'block' }}>sans fin</span>}</td>
                    <td>
                      <Etat table={STATUTS_FONDEMENT} code={f.statut} />
                      {(f.enregistre_par_nom || f.enregistre_le) && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>enregistré{f.enregistre_par_nom ? ` par ${f.enregistre_par_nom}` : ''}{f.enregistre_le ? ` le ${dateHeureLongue(f.enregistre_le)}` : ''}</span>}
                      {f.confirme_par_nom && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>confirmé par {f.confirme_par_nom}</span>}
                      {f.motif_retrait && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>retiré : {f.motif_retrait}</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {f.statut === 'A_CONFIRMER' && droits.a('ds_fondement_confirmer') && <Bouton petit variante="primaire" onClick={() => setFenetre({ type: 'confirmer', f })}>Confirmer</Bouton>}
                      {f.statut !== 'RETIRE' && (droits.a('ds_fondement_enregistrer') || droits.a('ds_fondement_confirmer')) && <Bouton petit variante="discret" onClick={() => setFenetre({ type: 'retirer', f })}>Retirer</Bouton>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationDs donnees={l.donnees} onPage={setPage} />
        </EtatsLecture>
      </section>
      {fenetre?.type === 'enregistrer' && <FenetreFondement organisation={organisation} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'confirmer' && <FenetreConfirmerFondement f={fenetre.f} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'retirer' && <FenetreRetirerFondement f={fenetre.f} onFermer={() => setFenetre(null)} onFait={fait} />}
    </>
  );
}
